/**
 * Action texto : deux choses comptent, quand (le scénario) et quoi (le texte).
 * Le nom interne et la phrase dite par l'agent ont une valeur par défaut et
 * restent modifiables sous « Options ». Des exemples prêts remplissent les
 * deux champs en un clic.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronDown, MessageSquare } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  smsActionSchema,
  newSMSAction,
  type SMSAction,
} from './actionsSchemas'

interface Props {
  isOpen: boolean
  /** Langue de l'agent : la phrase par défaut est dite dans cette langue, pas dans celle du dashboard. */
  agentLanguage?: string
  onClose: () => void
  onSave: (action: SMSAction) => void
  action?: SMSAction | null
}

export function SMSActionModal({ isOpen, agentLanguage, onClose, onSave, action }: Props) {
  const { t, i18n } = useTranslation()
  const [options, setOptions] = useState(false)

  const form = useForm<SMSAction>({
    resolver: zodResolver(smsActionSchema),
    defaultValues: action ?? newSMSAction(),
  })

  // Re-hydrate sur ouverture ou changement d'action éditée.
  const reponseParDefaut = i18n.getFixedT(agentLanguage === 'en' ? 'en' : 'fr')('manager.aiAgents.smsModal.agentResponseDefault', 'Je vous l’envoie par texto tout de suite.')
  // Nouvelle action : la phrase de l'agent est déjà remplie, on voit ce qui sera dit.
  useEffect(() => {
    if (isOpen) { form.reset(action ?? { ...newSMSAction(), agent_response: reponseParDefaut }); setOptions(false) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, action, form])

  const exemples = [
    { id: 'hours', label: t('manager.aiAgents.smsModal.ex.hours', 'Horaires'), scenario: t('manager.aiAgents.smsModal.ex.hoursScenario', 'Si l’appelant demande les heures d’ouverture'), message: t('manager.aiAgents.smsModal.ex.hoursMessage', 'Nos heures : lundi au vendredi, 9 h à 17 h. Samedi, 10 h à 14 h.') },
    { id: 'address', label: t('manager.aiAgents.smsModal.ex.address', 'Adresse'), scenario: t('manager.aiAgents.smsModal.ex.addressScenario', 'Si l’appelant demande où nous sommes ou comment venir'), message: t('manager.aiAgents.smsModal.ex.addressMessage', 'Notre adresse : 123 rue Principale, Montréal. Stationnement à l’arrière.') },
    { id: 'booking', label: t('manager.aiAgents.smsModal.ex.booking', 'Lien de rendez-vous'), scenario: t('manager.aiAgents.smsModal.ex.bookingScenario', 'Si l’appelant veut prendre rendez-vous lui-même en ligne'), message: t('manager.aiAgents.smsModal.ex.bookingMessage', 'Prenez rendez-vous ici : https://…') },
    { id: 'payment', label: t('manager.aiAgents.smsModal.ex.payment', 'Lien de paiement'), scenario: t('manager.aiAgents.smsModal.ex.paymentScenario', 'Si l’appelant veut payer ou régler une facture'), message: t('manager.aiAgents.smsModal.ex.paymentMessage', 'Vous pouvez payer en ligne ici : https://…') },
  ]
  const appliquer = (ex: (typeof exemples)[number]) => {
    form.setValue('scenario', ex.scenario, { shouldDirty: true })
    form.setValue('message_template', ex.message, { shouldDirty: true })
    form.setValue('name', ex.label, { shouldDirty: true })
  }

  const onSubmit = (values: SMSAction) => {
    onSave(values)
    onClose()
  }
  // Le nom et la phrase de l'agent ne sont pas demandés : on les remplit avant validation.
  const soumettre = (e: FormEvent<HTMLFormElement>) => {
    e.stopPropagation()
    if (!form.getValues('name').trim()) form.setValue('name', form.getValues('scenario').trim().slice(0, 60) || t('manager.aiAgents.smsModal.defaultName', 'Texto'))
    if (!form.getValues('agent_response').trim()) form.setValue('agent_response', reponseParDefaut)
    return form.handleSubmit(onSubmit)(e)
  }

  const errMsg = (key?: string) => (key ? t(`manager.aiAgents.errors.${key}`) : null)
  const longueur = (form.watch('message_template') || '').length

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl p-0 gap-0 max-h-[90vh] overflow-hidden">
        {/* stopPropagation : ce form est rendu dans un Portal Radix imbriqué
            visuellement dans le form parent (AIAgentFormView) ; sans ça,
            sauvegarder ici soumettrait aussi le form de l'agent. */}
        <form onSubmit={soumettre} className="flex flex-col max-h-[90vh]">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-500/10 dark:to-cyan-500/10">
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <div className="p-2 bg-blue-100 dark:bg-blue-500/20 rounded-lg">
                <MessageSquare className="h-5 w-5 text-blue-600" />
              </div>
              {action ? t('manager.aiAgents.smsModal.editTitle') : t('manager.aiAgents.smsModal.createTitle')}
            </DialogTitle>
          </DialogHeader>

          <div className="p-6 space-y-5 overflow-y-auto flex-1">
            {!action && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-500 mr-1">{t('manager.aiAgents.smsModal.examples', 'Exemples')}</span>
                {exemples.map((ex) => (
                  <button key={ex.id} type="button" onClick={() => appliquer(ex)}
                    className="rounded-full border border-slate-300 dark:border-slate-700 px-3 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 hover:border-blue-400 hover:text-blue-700">
                    {ex.label}
                  </button>
                ))}
              </div>
            )}

            <div>
              <Label htmlFor="sms-scenario" className="text-sm font-medium">{t('manager.aiAgents.smsModal.scenario', 'Quand l’envoyer')}</Label>
              <Textarea id="sms-scenario" placeholder={t('manager.aiAgents.smsModal.scenarioPlaceholder')} className="mt-2 min-h-[64px] resize-none" {...form.register('scenario', {
                onBlur: (e) => { if (!form.getValues('name').trim()) form.setValue('name', e.target.value.trim().slice(0, 60)) },
              })} />
              {form.formState.errors.scenario && <p className="text-xs text-red-500 mt-1">{errMsg(form.formState.errors.scenario.message)}</p>}
            </div>

            <div>
              <div className="flex items-baseline justify-between">
                <Label htmlFor="sms-template" className="text-sm font-medium">{t('manager.aiAgents.smsModal.messageTemplate', 'Le texto')}</Label>
                <span className={cn('text-xs', longueur > 160 ? 'text-amber-600' : 'text-slate-400')}>{longueur}/160</span>
              </div>
              <Textarea id="sms-template" placeholder={t('manager.aiAgents.smsModal.messageTemplatePlaceholder')} className="mt-2 min-h-[96px] resize-none" {...form.register('message_template')} />
              {form.formState.errors.message_template && <p className="text-xs text-red-500 mt-1">{errMsg(form.formState.errors.message_template.message)}</p>}
              <p className="text-xs text-slate-500 mt-1">{t('manager.aiAgents.smsModal.sentAsIs', 'Envoyé tel quel, au numéro de l’appelant.')}</p>
            </div>

            <div className="border-t dark:border-slate-800 pt-3">
              <button type="button" onClick={() => setOptions(!options)} className="inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white">
                <ChevronDown className={cn('h-4 w-4 transition-transform', options && 'rotate-180')} />{t('manager.aiAgents.smsModal.options', 'Options')}
              </button>
              {options && (
                <div className="mt-3 space-y-4">
                  <div>
                    <Label htmlFor="sms-response" className="text-sm font-medium">{t('manager.aiAgents.smsModal.agentResponse', 'Ce que l’agent dit en l’envoyant')}</Label>
                    <Input id="sms-response" className="mt-2" {...form.register('agent_response')} />
                  </div>
                  <div>
                    <Label htmlFor="sms-name" className="text-sm font-medium">{t('manager.aiAgents.smsModal.name', 'Nom interne')}</Label>
                    <Input id="sms-name" placeholder={t('manager.aiAgents.smsModal.namePlaceholder')} className="mt-2" {...form.register('name')} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="px-6 py-4 border-t dark:border-slate-800 flex justify-end gap-3 bg-gray-50 dark:bg-slate-800/50">
            <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
              {action ? t('manager.aiAgents.update') : t('manager.aiAgents.smsModal.create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
